import {
    memo, MouseEventHandler, SetStateAction, useCallback,
    useContext, useMemo, useState, ChangeEvent } from 'react';
import { Selection } from 'src/projects/types/Selection';
import { PATHS } from 'src/types/Paths';
import { getAppRoutePrefix } from 'src/projects/utils/getAppRoutePrefix';
import { OverflowMenu } from 'src/modules/common/components/OverflowMenu/OverflowMenu';
import { MoreIcon } from 'src/modules/common/icons/MoreIcon';
import { OverflowMenuItem } from 'src/modules/common/components/OverflowMenu/OverflowMenuItem';
import { I18n } from 'src/modules/common/components/I18n/I18n';
import { MESSAGES } from 'src/modules/common/constants';
import { CopyIcon } from 'src/modules/common/icons/CopyIcon';
import { DeleteIcon } from 'src/modules/common/icons/DeleteIcon';
import { ChevronUpIcon } from 'src/modules/common/icons/ChevronUpIcon';
import { ChevronDownIcon } from 'src/modules/common/icons/ChevronDownIcon';
import { FetchError } from 'src/projects/components/FetchError';
import { FetchValveSelection } from 'src/projects/components/FetchValveSelection';
import { FetchActuatorSelection } from 'src/projects/components/FetchActuatorSelection';
import { FetchSensorSelection } from 'src/projects/components/FetchSensorSelection';
import { RenameIcon } from 'src/modules/common/icons/RenameIcon.tsx';
import { RenameProjectModal } from 'src/projects/components/RenameProjectModal.tsx';
import { DuplicateIcon } from 'src/modules/common/icons/DuplicateIcon.tsx';
import { ExportIcon } from 'src/modules/common/icons/ExportIcon.tsx';
import { exportSelection } from 'src/projects/utils/exportSelection.ts';
import { ConfigContext } from 'src/configContext.ts';
import { useBelimoTranslate } from 'src/common/hooks/useBelimoTranslate.ts';
import { composeExcelBuffer } from 'src/projects/hooks/composeExcelBuffer.ts';
import { saveAs } from 'file-saver';
import { getExcelFileName } from 'src/utils/getExcelFileName.ts';
import { CartIcon } from 'src/modules/common/icons/CartIcon.tsx';
import { ExportTable } from 'shared/projects/ExportTable.ts';
import { CheckboxButton } from 'src/modules/common/components/Checkbox/CheckboxButton.tsx';
import { SelectionDataProvider } from 'src/projects/components/SelectionDataProvider.tsx';
import { SelectionSubtotal } from 'src/projects/components/SelectionSubtotal.tsx';
import { ProjectSelectionComment } from 'src/projects/components/ProjectSelectionComment.tsx';
import { useToast } from 'src/modules/common/components/Toast/useToast.ts';
import { FetchWebAppSelection } from 'src/projects/components/FetchWebAppSelection.tsx';
import { useProjectNavigation } from 'src/projects/navigation/ProjectNavigationContext.ts';
import { RuntimeContext } from 'src/runtimeContext.ts';
import { confirm } from 'src/projects/utils/confirmation.tsx';

type Props = {
    selection: Selection;
    openedSelection: string | null;
    setOpenedSelection: (name: SetStateAction<string | null>) => void;
    removeHandler: (selection: Selection) => void;
    duplicateHandler: (selection: Selection) => void;
    renameHandler: (selection: Selection, name: string) => void;
    isNameValid: (Selection: Selection, name: string) => boolean;
    addToCartHandler: (selection: Selection) => Promise<void>;
    updateSelection: (selection: Selection) => void;
    isSelected?: boolean;
    onToggleSelect?: (name: string) => void;
}

const ProjectSelectionItemComponent = ({
    selection,
    openedSelection,
    setOpenedSelection,
    removeHandler,
    duplicateHandler,
    renameHandler,
    isNameValid,
    addToCartHandler,
    updateSelection,
    isSelected = false,
    onToggleSelect,
}: Props) => {
    const { onNavigate } = useProjectNavigation();
    const { userSettings, selectionsDefaults } = useContext(ConfigContext);
    const { isMobile } = useContext(RuntimeContext);
    const translate = useBelimoTranslate();
    const { showToast } = useToast();
    const [modalState, setModalState] = useState<{
        opened: boolean;
        selection?: Selection;
    }>({
        opened: false,
    });

    const openUrl = useMemo(() => {
        const url = new URL(window.location.toString());
        url.pathname = getAppRoutePrefix(window.location, selection.type);
        if (selection.type.includes('retrofit')) {
            url.pathname = `${url.pathname}share`;
            url.search = `?s=${encodeURIComponent(JSON.stringify({
                searchQuery: selection.searchQuery,
                pathname: selection.pathname,
            }))}`;
        } else {
            url.hash = `${PATHS.Share}?s=${encodeURIComponent(JSON.stringify({
                searchQuery: selection.searchQuery,
                pathname: selection.pathname,
            }))}`;
        }
        return url.toString();
    }, [selection]);

    const editUrl = useMemo(() => {
        const url = new URL(window.location.toString());
        url.pathname = getAppRoutePrefix(window.location, selection.type);
        if (selection.type.includes('retrofit')) {
            url.pathname = `${url.pathname}share`;
            url.search = `?s=${encodeURIComponent(JSON.stringify({
                searchQuery: selection.searchQuery,
                pathname: selection.pathname,
                name: selection.name,
            }))}`;
        } else {
            url.hash = `${PATHS.Share}?s=${encodeURIComponent(JSON.stringify({
                searchQuery: selection.searchQuery,
                pathname: selection.pathname,
                name: selection.name,
            }))}`;
        }
        return url.toString();
    }, [selection]);

    const handleAddToCart: MouseEventHandler<HTMLButtonElement> = useCallback((e) => {
        e.stopPropagation();
        addToCartHandler(selection)
            // eslint-disable-next-line no-console
            .catch((err) => console.log(err));
    }, [selection, addToCartHandler]);

    const handleCopySelection: MouseEventHandler<HTMLButtonElement> = useCallback((e) => {
        e.stopPropagation();
        navigator.clipboard.writeText(openUrl)
            .then(() => showToast({
                type: 'info',
                isLowContrast: true,
                title: <I18n messageId={MESSAGES.copyLinkToastTitle}/>,
                message: <I18n messageId={MESSAGES.copyLinkSuccessMessage}/>,
            }))
            .catch(() => showToast({
                type: 'error',
                title: <I18n messageId={MESSAGES.copyLinkToastTitle}/>,
                message: <I18n messageId={MESSAGES.copyLinkErrorMessage}/>,
            }));
    }, [openUrl, showToast]);

    const handleRenameSelection: MouseEventHandler<HTMLButtonElement> = useCallback((e) => {
        e.stopPropagation();
        setModalState({ selection, opened: true });
    }, [selection]);
    const handleDuplicateSelection: MouseEventHandler<HTMLButtonElement> = useCallback((e) => {
        e.stopPropagation();
        duplicateHandler(selection);
    }, [duplicateHandler, selection]);

    const handleExportSelection: MouseEventHandler<HTMLButtonElement> = useCallback(async (e) => {
        e.stopPropagation();
        const data = exportSelection({ selection, t: translate, userSettings });

        const buffer = await composeExcelBuffer(data);
        saveAs(new Blob([buffer], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        }), getExcelFileName());
    }, [selection, translate, userSettings]);

    const handleRemoveSelection: MouseEventHandler<HTMLButtonElement> = useCallback((e) => {
        e.stopPropagation();
        confirm({
            header: MESSAGES.projectPageConfirmationModalHeader,
            message: MESSAGES.projectPageRemoveConfirmation,
        })
            .then((isConfirmed) => (isConfirmed ? removeHandler(selection) : Promise.resolve()))
            .catch(() => null);
    }, [removeHandler, selection]);

    const handleOpenSelection = useCallback(() => {
        setOpenedSelection((prev) => (prev === selection.name
            ? null
            : selection.name
        ));
    }, [selection.name, setOpenedSelection]);

    const handleCheckboxChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
        e.stopPropagation();
        onToggleSelect?.(selection.name);
    }, [onToggleSelect, selection.name]);

    const handleCloseModal = useCallback(() => {
        setModalState({ opened: false });
    }, [setModalState]);

    const handleSaveModal = useCallback((name: string) => {
        renameHandler(selection, name);
        handleCloseModal();
    }, [selection, handleCloseModal, renameHandler]);

    const handleUpdateSelection = useCallback((searchQuery: string, exportData: ExportTable) => {
        updateSelection({
            ...selection,
            searchQuery,
            exportData,
        });
    }, [selection, updateSelection]);

    const handleWebAppUpdateSelection = useCallback((data: Selection) => {
        updateSelection({
            ...data,
        });
    }, [updateSelection]);

    const typeMessageId = ({
        valves: MESSAGES.Valves,
        actuators: MESSAGES.Actuators,
        sensors: MESSAGES.Sensors,
        retrofit_valves: MESSAGES.Valves,
        retrofit_actuators: MESSAGES.Actuators,
        retrofit_sensors: MESSAGES.Sensors,
    } as const)[selection.type] ?? MESSAGES.Selection;

    const isOpen = openedSelection === selection.name;

    return (
        <SelectionDataProvider selection={selection}>
            <div className="vss-project-selection-item">
                {modalState.opened && (
                    <RenameProjectModal
                        selection={selection}
                        opened={modalState.opened}
                        handleClose={handleCloseModal}
                        onSave={handleSaveModal}
                        isNameValid={isNameValid}
                    />
                )}
                <div className="vss-project-selection-item__header">
                    <div className="vss-project-selection-item__left">
                        {onToggleSelect && (
                        // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
                            <div
                                className="vss-project-selection-item__checkbox"
                                onClick={(e) => e.stopPropagation()}
                            >
                                <CheckboxButton
                                    id={`selection-checkbox-${selection.name}`}
                                    checked={isSelected}
                                    onChange={handleCheckboxChange}
                                />
                            </div>
                        )}

                        <div className="vss-project-selection-item__title-group">
                            <a
                                className="vss-project-selection-item__title"
                                href={editUrl}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    e.preventDefault();
                                    onNavigate(editUrl);
                                }}
                            >
                                {selection.name}
                            </a>
                            <div className="vss-project-selection-item__subtitle">
                                <I18n messageId={typeMessageId}/>
                            </div>
                        </div>
                    </div>

                    <div className="vss-project-selection-item__details">
                        {!isMobile && <SelectionSubtotal withTitle={false}/>}
                        <OverflowMenu
                            kind="ghost-secondary"
                            size="sm"
                            icon={<MoreIcon/>}
                        >
                            <OverflowMenuItem
                                size="md"
                                onClick={handleRenameSelection}
                                reversed={true}
                                icon={(
                                    <div className="vss-project-selection-item__icon">
                                        <RenameIcon/>
                                    </div>
                                )}
                            >
                                <I18n messageId={MESSAGES.projectPageRenameSelection}/>
                            </OverflowMenuItem>

                            <OverflowMenuItem
                                size="md"
                                onClick={handleCopySelection}
                                reversed={true}
                                icon={(
                                    <div className="vss-project-selection-item__icon">
                                        <CopyIcon/>
                                    </div>
                                )}
                            >
                                <I18n messageId={MESSAGES.projectPageCopySelection}/>
                            </OverflowMenuItem>

                            <OverflowMenuItem
                                size="md"
                                onClick={handleExportSelection}
                                reversed={true}
                                icon={(
                                    <div className="vss-project-selection-item__icon">
                                        <ExportIcon/>
                                    </div>
                                )}
                            >
                                <I18n messageId={MESSAGES.projectPageExportSelection}/>
                            </OverflowMenuItem>

                            <OverflowMenuItem
                                size="md"
                                onClick={handleDuplicateSelection}
                                reversed={true}
                                icon={(
                                    <div className="vss-project-selection-item__icon">
                                        <DuplicateIcon/>
                                    </div>
                                )}
                            >
                                <I18n messageId={MESSAGES.projectPageDuplicateSelection}/>
                            </OverflowMenuItem>
                            {selectionsDefaults.addToCartButtonEnabled && (
                                <OverflowMenuItem
                                    size="md"
                                    onClick={handleAddToCart}
                                    reversed={true}
                                    icon={(
                                        <div className="vss-project-selection-item__icon">
                                            <CartIcon/>
                                        </div>
                                    )}
                                >
                                    <I18n messageId={MESSAGES.projectPageAddToCartSelection}/>
                                </OverflowMenuItem>
                            )}

                            <OverflowMenuItem
                                size="md"
                                kind="error"
                                onClick={handleRemoveSelection}
                                reversed={true}
                                icon={(
                                    <div className="vss-project-selection-item__icon">
                                        <DeleteIcon/>
                                    </div>
                                )}
                            >
                                <I18n messageId={MESSAGES.projectPageRemoveSelection}/>
                            </OverflowMenuItem>
                        </OverflowMenu>

                        <button
                            type="button"
                            className="vss-project-selection-item__toggle"
                            onClick={handleOpenSelection}
                            aria-label="Toggle selection details"
                            aria-expanded={isOpen}
                        >
                            {isOpen
                                ? <ChevronUpIcon/>
                                : <ChevronDownIcon/>}
                        </button>
                    </div>
                </div>

                <div
                    className="vss-project-selection-item__body"
                    style={{ display: isOpen
                        ? 'flex'
                        : 'none',
                    }}
                >
                    {isOpen && (
                        <FetchError fallback="Issue with loading selection details">
                            {selection.type === 'valves' && (
                                <FetchValveSelection
                                    exportData={selection.exportData}
                                    searchParams={selection.searchQuery}
                                    pathname={selection.pathname}
                                    updateSelection={handleUpdateSelection}
                                >
                                    <ProjectSelectionComment selection={selection}/>
                                </FetchValveSelection>
                            )}
                            {selection.type === 'actuators' && (
                                <FetchActuatorSelection
                                    exportData={selection.exportData}
                                    searchParams={selection.searchQuery}
                                    pathname={selection.pathname}
                                    updateSelection={handleUpdateSelection}
                                >
                                    <ProjectSelectionComment selection={selection}/>
                                </FetchActuatorSelection>
                            )}
                            {selection.type === 'sensors' && (
                                <FetchSensorSelection
                                    exportData={selection.exportData}
                                    searchParams={selection.searchQuery}
                                    pathname={selection.pathname}
                                    updateSelection={handleUpdateSelection}
                                >
                                    <ProjectSelectionComment selection={selection}/>
                                </FetchSensorSelection>
                            )}
                            {selection.type === 'retrofit_actuators' && (
                                <FetchWebAppSelection
                                    selection={selection}
                                    updateSelection={handleWebAppUpdateSelection}
                                />
                            )}
                            {selection.type === 'retrofit_valves' && (
                                <FetchWebAppSelection
                                    selection={selection}
                                    updateSelection={handleWebAppUpdateSelection}
                                />
                            )}
                            {selection.type === 'retrofit_sensors' && (
                                <FetchWebAppSelection
                                    selection={selection}
                                    updateSelection={handleWebAppUpdateSelection}
                                />
                            )}
                        </FetchError>
                    )}
                </div>
                {isMobile && <SelectionSubtotal withTitle={true}/>}
            </div>
        </SelectionDataProvider>
    );
};

/**
 * Re-render a row only when something it actually shows changes. Without this,
 * every persist (duplicate/rename/remove) hands each row brand-new `selection`
 * objects (convertSelection) and the default shallow `memo` re-renders all rows.
 */
const arePropsEqual = (prev: Props, next: Props): boolean => {
    const prevOpen = prev.openedSelection === prev.selection.name;
    const nextOpen = next.openedSelection === next.selection.name;
    return (
        prevOpen === nextOpen &&
        prev.isSelected === next.isSelected &&
        prev.selection.name === next.selection.name &&
        prev.selection.type === next.selection.type &&
        prev.selection.pathname === next.selection.pathname &&
        prev.selection.searchQuery === next.selection.searchQuery &&
        prev.selection.exportData === next.selection.exportData &&
        prev.setOpenedSelection === next.setOpenedSelection &&
        prev.removeHandler === next.removeHandler &&
        prev.duplicateHandler === next.duplicateHandler &&
        prev.renameHandler === next.renameHandler &&
        prev.isNameValid === next.isNameValid &&
        prev.addToCartHandler === next.addToCartHandler &&
        prev.updateSelection === next.updateSelection &&
        prev.onToggleSelect === next.onToggleSelect
    );
};

export const ProjectSelectionItem = memo(ProjectSelectionItemComponent, arePropsEqual);
